import { ArrowRight, ShieldCheck, BadgePercent } from "lucide-react";
import { useI18n } from "@/i18n/I18nProvider";
import { useRates, formatRate } from "@/hooks/useRates";

const HERO_IMG =
  "https://images.unsplash.com/photo-1600596542815-ffad4c1539a9?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjAzNDR8MHwxfHNlYXJjaHwxfHxtb2Rlcm4lMjBob3VzZSUyMGV4dGVyaW9yfGVufDB8fHx8MTc3NzAyOTM1OHww&ixlib=rb-4.1.0&q=85";

export const Hero = () => {
  const { t } = useI18n();
  const { data: rates } = useRates();

  const fix = rates?.fixation_years ?? 5;
  const rateValue = rates?.min_rate ?? 4.9;
  const valueDisplay = `${t.hero.rates_value_prefix} ${formatRate(rateValue)}`;
  const noteDisplay = t.hero.rates_note_suffix(fix, "");
  const sourceLabel = rates?.source && rates.source !== "fallback"
    ? `${t.hero.rates_source_prefix} ${rates.source}`
    : "";
  return (
    <section
      id="top"
      data-testid="hero-section"
      className="relative pt-28 md:pt-36 pb-20 md:pb-28 overflow-hidden"
    >
      <div className="max-w-[1400px] mx-auto px-6 md:px-10">
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-10 lg:gap-16 items-end">
          <div className="lg:col-span-7 fade-up">
            <span
              className="overline text-muted-foreground"
              data-testid="hero-overline"
            >
              {t.hero.overline}
            </span>
            <h1
              className="mt-6 font-heading font-semibold text-5xl sm:text-6xl lg:text-7xl leading-[0.95] tracking-tight text-foreground"
              data-testid="hero-heading"
            >
              {t.hero.title_1}{" "}
              <span className="italic font-light text-primary">
                {t.hero.title_em}
              </span>
              <br />
              {t.hero.title_2}
            </h1>
            <p
              className="mt-8 max-w-xl text-lg md:text-xl text-muted-foreground leading-relaxed"
              data-testid="hero-subtitle"
            >
              {t.hero.subtitle}
            </p>

            <div className="mt-10 flex flex-wrap gap-4">
              <a
                href="#calculator"
                data-testid="hero-cta-calculator"
                className="inline-flex items-center gap-2 px-6 py-3.5 bg-primary text-primary-foreground rounded-full text-sm font-medium hover:bg-foreground transition-colors"
              >
                {t.hero.cta_calc}
                <ArrowRight className="w-4 h-4" />
              </a>
              <a
                href="#contact"
                data-testid="hero-cta-contact"
                className="inline-flex items-center gap-2 px-6 py-3.5 border border-foreground/20 text-foreground rounded-full text-sm font-medium hover:bg-foreground hover:text-background transition-colors"
              >
                {t.hero.cta_contact}
              </a>
            </div>

            <div className="mt-14 grid grid-cols-2 gap-8 max-w-md">
              <Stat value="10+" label={t.hero.stat_banks} testid="stat-banks" />
              <Stat value="4.9/5" label={t.hero.stat_rating} testid="stat-rating" />
            </div>
          </div>

          <div className="lg:col-span-5 relative">
            <div className="relative aspect-[4/5] md:aspect-[3/4] overflow-hidden rounded-sm">
              <img
                src={HERO_IMG}
                alt="dbrinvest.cz"
                className="absolute inset-0 w-full h-full object-cover"
                data-testid="hero-image"
              />
              <div className="absolute inset-0 bg-gradient-to-t from-foreground/30 via-transparent to-transparent" />
            </div>

            <div className="absolute -left-6 md:-left-10 bottom-10 bg-card border border-border rounded-sm p-5 w-64 shadow-lg">
              <div className="flex items-center gap-2 overline text-primary">
                <BadgePercent className="w-4 h-4" /> {t.hero.rates_overline}
              </div>
              <div
                className="mt-3 font-heading text-3xl font-semibold text-foreground"
                data-testid="hero-rate-value"
              >
                {valueDisplay}
              </div>
              <div
                className="mt-1 text-xs text-muted-foreground"
                data-testid="hero-rate-note"
              >
                {noteDisplay}
              </div>
              {sourceLabel && (
                <div
                  className="mt-2 text-[10px] uppercase tracking-wider text-muted-foreground/70"
                  data-testid="hero-rate-source"
                >
                  {sourceLabel}
                </div>
              )}
            </div>

            <div className="hidden md:flex absolute -right-4 top-8 bg-foreground text-background rounded-sm p-4 w-52 items-start gap-3">
              <ShieldCheck className="w-5 h-5 shrink-0 mt-0.5 text-secondary" />
              <p className="text-xs leading-relaxed">{t.hero.trust}</p>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
};

const Stat = ({ value, label, testid }) => (
  <div data-testid={`hero-${testid}`}>
    <div className="font-heading text-2xl md:text-3xl font-semibold text-foreground">
      {value}
    </div>
    <div className="mt-1 text-xs text-muted-foreground">{label}</div>
  </div>
);

export default Hero;
